import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { isAdmin } from '@/utils/subscription';
import ProPaywallModal from '@/components/ProPaywallModal';
import { OaWorkspaceShell } from '../components/OaWorkspaceShell';
import { 
  Check, 
  ShieldCheck, 
  Sparkles, 
  Zap, 
  Building2, 
  CreditCard 
} from 'lucide-react';

export function OncoAgentBilling(): React.ReactElement {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly' | 'lifetime'>('yearly');

  const plans = [
    {
      id: 'monthly' as const,
      name: 'Starter Pilot',
      tagline: 'Ideal for single-clinician clinics',
      price: '$19.99',
      period: '/month',
      compare: '$39.99',
      features: [
        'Unlimited referral packets',
        'Automatic M1 pathway auto-routing',
        'Full document extraction',
        'Real-time status tracking'
      ],
      cta: 'Subscribe Starter',
      icon: Zap,
      accent: 'text-blue-600 bg-blue-50'
    },
    {
      id: 'yearly' as const,
      name: 'Professional Team',
      tagline: 'Best for active oncology practices',
      price: '$14.99',
      period: '/month',
      compare: '$19.99',
      note: 'Billed $179.88 annually (Save 25%)',
      features: [
        'Everything in Starter Pilot',
        'Advanced clinical red flag warnings',
        'Custom pathway CDS rules',
        'Integrated clinical safety logs',
        'Priority SLA tracking support'
      ],
      cta: 'Subscribe Professional',
      icon: Sparkles,
      accent: 'text-[#0f3e17] bg-emerald-50 border-2 border-[#0f3e17]/20',
      featured: true
    },
    {
      id: 'lifetime' as const,
      name: 'Enterprise Clinic',
      tagline: 'Complete access, no recurring billing',
      price: '$999',
      period: ' once',
      compare: '$2,399',
      features: [
        'Everything in Professional Team',
        'Lifetime unlimited clinical handoffs',
        'Dedicated clinic API key access',
        'Custom EHR & FHIR mapping rules',
        '24/7 dedicated support desk'
      ],
      cta: 'Get Lifetime Access',
      icon: Building2,
      accent: 'text-amber-700 bg-amber-50'
    }
  ];

  return (
    <OaWorkspaceShell
      kicker="Plan & Subscriptions"
      title="Choose your workspace tier"
      subtitle="All plans unlock unlimited referrals, intelligent clinical pathway auto-routing, and real-time triage queue tracking."
    >
      <div className="space-y-8 animate-fade-in">
        {/* Admin or Active Seat Banner */}
        {admin ? (
          <div className="bg-emerald-50/50 border-2 border-[#0f3e17]/20 rounded-2xl p-6 shadow-sm flex items-start gap-4">
            <div className="bg-[#0f3e17]/10 p-3.5 rounded-xl text-[#0f3e17]">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <span className="bg-[#0f3e17] text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                Admin Seat Active
              </span>
              <h3 className="font-serif font-semibold text-lg text-[#161616] mt-2 mb-1">
                Full Operations Access Enabled
              </h3>
              <p className="text-xs text-[#6d6a63] leading-relaxed">
                Your administrative account (<strong className="text-[#161616] font-medium">{user?.email}</strong>) has full developer and administrative credentials. Pricing checkouts, billing paywalls, and pathways are unlocked globally across this workspace without requiring checkout.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-amber-50/40 border border-amber-200 rounded-2xl p-5 shadow-xs flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-amber-700" />
              <div className="text-xs text-amber-900 font-medium">
                Your clinic workspace is currently running on a trial tier. Subscribe below to ensure uninterrupted service.
              </div>
            </div>
          </div>
        )}

        {/* 3-Column Plan Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          {plans.map((plan) => {
            const Icon = plan.icon;
            const isFeatured = plan.featured;
            return (
              <div 
                key={plan.id}
                className={`bg-white border rounded-2xl p-6 shadow-sm flex flex-col justify-between transition-all duration-300 hover:shadow-md relative ${
                  isFeatured 
                    ? 'border-[#0f3e17] ring-1 ring-[#0f3e17]/10 scale-[1.01]' 
                    : 'border-[#e6e2d8]'
                }`}
              >
                {isFeatured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0f3e17] text-white text-[9px] font-extrabold px-3 py-1 rounded-full uppercase tracking-widest shadow-xs">
                    Recommended
                  </span>
                )}
                
                <div className="space-y-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-base text-[#161616]">{plan.name}</h3>
                      <p className="text-xs text-[#6d6a63] mt-0.5">{plan.tagline}</p>
                    </div>
                    <div className={`p-2 rounded-lg ${plan.accent.split(' ').slice(0,2).join(' ')}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                  </div>

                  <div className="border-t border-[#f6f4ef] pt-4">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-serif font-bold text-[#161616]">{plan.price}</span>
                      <span className="text-xs text-[#6d6a63]">{plan.period}</span>
                    </div>
                    <p className="text-[11px] text-[#6d6a63] mt-1">
                      Compare: <span className="line-through">{plan.compare}</span> {plan.note && `· ${plan.note}`}
                    </p>
                  </div>

                  <ul className="space-y-2.5 pt-2 border-t border-[#f6f4ef]">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-xs text-[#6d6a63] leading-relaxed">
                        <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-6 border-t border-[#f6f4ef] mt-6">
                  {admin ? (
                    <button
                      type="button"
                      className="w-full text-center bg-[#f6f4ef] text-[#0f3e17] text-xs font-semibold py-2.5 rounded-xl border border-[#e6e2d8] cursor-not-allowed opacity-80"
                      disabled
                    >
                      Unlocked via Admin Seat
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={`w-full text-center text-xs font-semibold py-2.5 rounded-xl transition-all ${
                        isFeatured 
                          ? 'bg-[#0f3e17] hover:bg-[#0c3312] text-white shadow-sm' 
                          : 'bg-white hover:bg-[#f6f4ef] text-[#161616] border border-[#e6e2d8]'
                      }`}
                      onClick={() => {
                        setSelectedPlan(plan.id);
                        setPaywallOpen(true);
                      }}
                    >
                      {plan.cta}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <ProPaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        onUnlocked={() => {}}
        returnPath="/oncoagent/app/billing"
        title="OncoAgent Workspace Subscription"
        subtitle="Secure checkout for Monthly, Yearly, or Lifetime access tiers."
      />
    </OaWorkspaceShell>
  );
}

export default OncoAgentBilling;
